import type Keycloak from 'keycloak-js';

export interface AuthTokenDiagnostics {
    authenticated: boolean;
    refreshTokenPresent: boolean;
    accessTokenPresent: boolean;
    accessTokenExp?: string;
    accessTokenExpiresInSeconds?: number;
    refreshTokenExp?: string;
    refreshTokenExpiresInSeconds?: number;
    isNearExpiry?: boolean;
    minValidity?: number;
    sessionId?: string;
    timeSkew?: number | null;
    subject?: string;
}

export function buildAuthTokenDiagnostics(
    keycloak: Keycloak | undefined,
    minValidity?: number
): AuthTokenDiagnostics {
    const nowSeconds = Math.floor(Date.now() / 1000);
    const timeSkew = keycloak?.timeSkew ?? null;

    const accessTokenExpiresInSeconds =
        keycloak?.tokenParsed?.exp !== undefined
            ? keycloak.tokenParsed.exp - nowSeconds + (timeSkew ?? 0)
            : undefined;

    const refreshTokenExpiresInSeconds =
        keycloak?.refreshTokenParsed?.exp !== undefined
            ? keycloak.refreshTokenParsed.exp - nowSeconds + (timeSkew ?? 0)
            : undefined;

    let isNearExpiry: boolean | undefined;
    if (keycloak && minValidity !== undefined) {
        try {
            isNearExpiry = keycloak.isTokenExpired(minValidity);
        } catch {
            isNearExpiry = undefined;
        }
    }

    return {
        authenticated: !!keycloak?.authenticated,
        refreshTokenPresent: !!keycloak?.refreshToken,
        accessTokenPresent: !!keycloak?.token,
        accessTokenExp:
            keycloak?.tokenParsed?.exp !== undefined
                ? new Date(keycloak.tokenParsed.exp * 1000).toISOString()
                : undefined,
        accessTokenExpiresInSeconds,
        refreshTokenExp:
            keycloak?.refreshTokenParsed?.exp !== undefined
                ? new Date(keycloak.refreshTokenParsed.exp * 1000).toISOString()
                : undefined,
        refreshTokenExpiresInSeconds,
        isNearExpiry,
        minValidity,
        sessionId: keycloak?.sessionId,
        timeSkew,
        subject: keycloak?.subject,
    };
}

export async function formatRefreshError(error: unknown): Promise<Record<string, unknown>> {
    const details: Record<string, unknown> = {
        errorType: error instanceof Error ? error.constructor.name : typeof error,
        message: error instanceof Error ? error.message : String(error),
    };

    if (error instanceof Error && error.cause !== undefined) {
        details.cause =
            error.cause instanceof Error ? error.cause.message : String(error.cause);
    }

    const response = getErrorResponse(error);
    if (!response) {
        return details;
    }

    details.httpStatus = response.status;
    details.httpStatusText = response.statusText;

    try {
        const contentType = response.headers.get('content-type') ?? '';
        const bodyText = await response.clone().text();

        if (contentType.includes('application/json') && bodyText) {
            details.httpBody = JSON.parse(bodyText);
        } else if (bodyText) {
            details.httpBodyText = bodyText;
        }
    } catch (bodyError) {
        details.httpBodyReadError =
            bodyError instanceof Error ? bodyError.message : String(bodyError);
    }

    return details;
}

function getErrorResponse(error: unknown): Response | undefined {
    if (typeof error !== 'object' || error === null || !('response' in error)) {
        return undefined;
    }

    const response = (error as { response?: unknown }).response;
    return response instanceof Response ? response : undefined;
}
